<template>
  <div>
    <div class="md:flex flex-wrap md:justify-center lg:justify-between justify-between">
      <div class="lg:w-5/12 w-full md:w-8/12">
        <h1 class="font-bold pt-5 lg:text-left text-center">
          My Profile
        </h1>
        <section class="py-4">
          <h2 class="mb-2 text-xl font-semibold text-gray-900">
            {{ profile.fullName }}
          </h2>
          <p class="text-gray-900">{{ profile.email }}</p>
        </section>
        <hr class="my-8">
        <section>
          <div class="flex flex-wrap items-center gap-6">
            <div class="w-full lg:w-3/12 md:w-4/12 text-center">
              <div class="mb-4">
                <d-avatar :text="profile.fullName" :img-src="thumbnailUrl" color="orange" size="100px" />
              </div>
              <d-btn v-if="form.profileImageUrl" key="removeimage" color="red" class="mt-1" size="sm" @click="showDeleteImagePrompt()">
                Reset Image
              </d-btn>
            </div>
            <div class="w-full md:w-8/12 lg:w-8/12">
              <h5 class="mb-4 font-bold text-center md:text-left text-gray-900">
                Profile Picture
              </h5>
              <input ref="imageUpload" type="file" style="display: none" @change="uploadImage">
              <div class="space-y-2">
                <d-btn class="bg-secondary text-white w-full md:w-auto" size="md" :loading="uploadingFile" @click="$refs.imageUpload.click()">
                  {{ form.profileImageUrl ? 'Change Image' : 'Upload Image' }}
                </d-btn>
                <p class="text-gray-900 text-sm mt-2">
                  Upload a profile picture to personalize your account. Recommended size: 200x200px
                </p>
              </div>
            </div>
          </div>
        </section>
        <hr class="my-8">

        <!-- Contact Information Form -->
        <section>
          <h5 class="mb-4 font-bold text-gray-900">
            Contact Information
          </h5>
          <d-message v-if="contactInformationError" type="error" class="mb-4">
            {{ contactInformationError }}
          </d-message>
          <d-message v-if="successMessage" type="success" class="mb-4">
            {{ successMessage }}
          </d-message>
          <form @submit.prevent="saveProfile" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="firstName" class="block text-sm font-medium text-gray-900 mb-1">First Name</label>
                <input 
                  id="firstName"
                  v-model="form.firstName" 
                  type="text" 
                  required 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring focus:ring-primary focus:border-primary"
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label for="lastName" class="block text-sm font-medium text-gray-900 mb-1">Last Name</label>
                <input 
                  id="lastName"
                  v-model="form.lastName" 
                  type="text" 
                  required 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring focus:ring-primary focus:border-primary"
                  placeholder="Enter last name"
                />
              </div>
            </div>
            
            <div>
              <label for="phoneNumber" class="block text-sm font-medium text-gray-900 mb-1">Phone Number</label>
              <input 
                id="phoneNumber"
                v-model="form.phoneNumber" 
                type="tel" 
                class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring focus:ring-primary focus:border-primary"
                placeholder="Enter phone number (optional)"
              />
            </div>
            
            <d-btn type="submit" :loading="savingChanges" class="bg-primary text-white">
              Save Changes
            </d-btn>
          </form>
        </section>
        <hr class="my-8">

        <!-- Email Change Section -->
        <section>
          <h5 class="mb-4 font-bold text-gray-900">
            Email Address
          </h5>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p class="text-sm text-gray-900 mb-2">
              <strong>Current Email:</strong> {{ profile.email }}
            </p>
            <p class="text-sm text-gray-900">
              <strong>Important:</strong> Changing your email address may affect your access to content based on Subject Area Access Rules. You will receive confirmation emails at both your current and new email addresses.
            </p>
          </div>

          <d-message v-if="emailChangeError" type="error" class="mb-4">
            {{ emailChangeError }}
          </d-message>
          
          <d-message v-if="emailChangeSuccess" type="success" class="mb-4">
            {{ emailChangeSuccess }}
          </d-message>
          
          <d-message v-if="emailChangePending" type="info" class="mb-4">
            Email change request sent! Please check your new email address ({{ pendingNewEmail }}) for a confirmation link.
          </d-message>

          <div v-if="!emailChangePending">
            <form @submit.prevent="requestEmailChange" class="space-y-4">
              <div>
                <label for="newEmail" class="block text-sm font-medium text-gray-900 mb-1">New Email Address</label>
                <input 
                  id="newEmail"
                  v-model="newEmail" 
                  type="email" 
                  required 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring focus:ring-primary focus:border-primary"
                  placeholder="Enter new email address"
                />
              </div>
              
              <d-btn type="submit" :loading="requestingEmailChange" class="bg-primary text-white">
                Request Email Change
              </d-btn>
            </form>
          </div>
        </section>
        <hr class="my-8">

        <div class="my-12 flex flex-wrap items-center gap-6">
          <div class="w-full lg:w-auto">
            <d-btn outline class="border-primary text-primary px-6" href="/ManageAccount/ChangePassword">
              Change Password
            </d-btn>
          </div>
          <div class="lg:flex-1 w-full">
            <p class="text-gray-900 text-sm mb-0">
              Change your account password to make it stronger or change it to keep your account secure
            </p>
          </div>
        </div>
        <div class="pt-6 border-t">
          <p class="text-sm text-gray-900">
            When utilizing this platform, you agree to abide by the 
            <u><a href="/legal/user-agreement.pdf" class="text-primary hover:text-primary-dark">user agreement</a></u>
          </p>
        </div>
      </div>
      <div class="lg:w-6/12 w-full md:w-10/12 lg:pl-8">
        <div class="bg-gray-50 rounded-2xl p-8 min-h-96 mb-20 shadow-sm">
          <!-- Simple Tabs Implementation -->
          <div class="border-b border-gray-200 mb-6">
            <nav class="-mb-px flex space-x-8">
              <button
                id="tab-btn-0"
                @click="switchTab(0)"
                class="py-2 px-1 border-b-2 font-medium text-sm border-primary text-primary"
              >
                Earned Certificates
              </button>
              <button
                id="tab-btn-1"
                @click="switchTab(1)"
                class="py-2 px-1 border-b-2 font-medium text-sm border-transparent text-primary hover:text-gray-700 hover:border-gray-300"
              >
                Transcript
              </button>
              <button
                id="tab-btn-2"
                @click="switchTab(2)"
                class="py-2 px-1 border-b-2 font-medium text-sm border-transparent text-primary hover:text-gray-700 hover:border-gray-300"
              >
                Interactions
              </button>
              <button
                id="tab-btn-3"
                @click="switchTab(3)"
                class="py-2 px-1 border-b-2 font-medium text-sm border-transparent text-primary hover:text-gray-700 hover:border-gray-300"
              >
                Purchases
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div >
            <!-- Earned Certificates Tab -->
            <div id="tab-content-0" >
              <h3 class="font-bold text-2xl mb-6">
                Earned Certificates (<span id="certificates-count">{{ earnedCertificates.length }}</span>)
              </h3>
              
              <div id="certificates-loading" class="flex justify-center py-10" style="display: none;">
                <div class="text-gray-600">Loading certificates...</div>
              </div>
              
              <div id="certificates-empty" class="text-center py-8 text-gray-700" style="display: none;">
                You haven't earned any certificates yet
              </div>
              
              <div id="certificates-list" class="space-y-6"></div>
            </div>

            <!-- Transcript Tab -->
            <div id="tab-content-1" style="display: none;">
              <h3 class="font-bold text-2xl mb-6">
                Transcript
              </h3>
              
              <div id="transcript-loading" class="flex justify-center py-10" style="display: none;">
                <div class="text-gray-600">Loading transcript...</div>
              </div>
              
              <div id="transcript-empty" class="text-center py-8 text-gray-700" style="display: none;">
                You have not earned any certificates yet. The ability to generate a transcript will be available once you have earned a certificate.
              </div>
              
              <div id="transcript-content" style="display: none;">
                <transcript-table :earned-certificates="earnedCertificates" />
              </div>
            </div>

            <!-- Interactions Tab -->
            <div id="tab-content-2" style="display: none;">
              <h3 class="font-bold text-2xl mb-6">
                Interactions (<span id="interactions-count">{{ interactions.length }}</span>)
              </h3>
              
              <div id="interactions-loading" class="flex justify-center py-10" style="display: none;">
                <div class="text-gray-600">Loading interactions...</div>
              </div>
              
              <div id="interactions-empty" class="text-center py-8 text-gray-700" style="display: none;">
                You haven't interacted with any content yet
              </div>
              
              <div id="interactions-list" class="space-y-6"></div>
            </div>

            <!-- Purchases Tab -->
            <div id="tab-content-3"  style="display: none;">
              <h3 class="font-bold text-2xl mb-6">
                Purchases (<span id="purchases-count">{{ purchases.length }}</span>)
              </h3>
              
              <div id="purchases-loading" class="flex justify-center py-10" style="display: none;">
                <div class="text-gray-600">Loading purchases...</div>
              </div>
              
              <div id="purchases-empty" class="text-center py-8 text-gray-700" style="display: none;">
                You haven't made any purchases yet
              </div>
              
              <div id="purchases-list" class="space-y-6"></div>
              
              <!-- Pagination for purchases -->
              <div v-if="total > pagination.pageSize" class="flex justify-center mt-6">
                <d-pagination :total-items="total" :per-page="pagination.pageSize" v-model:current="pagination.page" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed, onMounted, watch } from 'vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useAuthStore } from '@stores/auth';
  import TranscriptTable from "./Components/TranscriptTable.vue";

  // Global functions for tab functionality
  window.showTab = function(tabIndex) {
    // Hide all tab content
    for (let i = 0; i < 4; i++) {
      const content = document.getElementById('tab-content-' + i);
      const button = document.getElementById('tab-btn-' + i);
      if (content) content.style.display = 'none';
      if (button) {
        button.className = 'py-2 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300';
      }
    }
    
    // Show selected tab content
    const selectedContent = document.getElementById('tab-content-' + tabIndex);
    const selectedButton = document.getElementById('tab-btn-' + tabIndex);
    if (selectedContent) selectedContent.style.display = 'block';
    if (selectedButton) {
      selectedButton.className = 'py-2 px-1 border-b-2 font-medium text-sm border-primary text-primary';
    }
  };

  // Function to populate certificate data
  window.populateCertificates = function(certificates) {
    const list = document.getElementById('certificates-list');
    const count = document.getElementById('certificates-count');
    const loading = document.getElementById('certificates-loading');
    const empty = document.getElementById('certificates-empty');
    
    if (loading) loading.style.display = 'none';
    if (count) count.textContent = certificates.length;
    
    if (certificates.length === 0) {
      if (empty) empty.style.display = 'block';
      if (list) list.style.display = 'none';
    } else {
      if (empty) empty.style.display = 'none';
      if (list) {
        list.style.display = 'block';
        list.innerHTML = certificates.map(cert => `
          <div class="bg-white rounded-lg p-6 shadow-sm">
            <div class="text-xl font-bold mb-3">${cert.completedActivityName || 'Certificate'}</div>
            <div class="flex flex-wrap items-center justify-between">
              <div class="text-sm text-gray-700">
                <div><strong>Date Completed:</strong> ${cert.completedDate || 'N/A'}</div>
                <div><strong>Total Hours:</strong> ${cert.completionLength || 'N/A'}</div>
              </div>
              <div class="mt-3 lg:mt-0">
                <a href="${cert.certificateUrl || '#'}" target="_blank" class="inline-flex items-center px-3 py-2 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-gray-50">
                  Download Certificate
                </a>
              </div>
            </div>
          </div>
        `).join('');
      }
    }
  };

  // Function to populate interactions data
  window.populateInteractions = function(interactions) {
    const list = document.getElementById('interactions-list');
    const count = document.getElementById('interactions-count');
    const loading = document.getElementById('interactions-loading');
    const empty = document.getElementById('interactions-empty');
    
    if (loading) loading.style.display = 'none';
    if (count) count.textContent = interactions.length;
    
    if (interactions.length === 0) {
      if (empty) empty.style.display = 'block';
      if (list) list.style.display = 'none';
    } else {
      if (empty) empty.style.display = 'none';
      if (list) {
        list.style.display = 'block';
        list.innerHTML = interactions.map(interaction => `
          <div class="bg-white rounded-lg p-6 shadow-sm">
            <div class="text-xl font-bold mb-3">${interaction.contentListingName || 'Content'}</div>
            <div class="flex flex-wrap items-center justify-between">
              <div class="text-sm text-gray-700">
                <div><strong>Last Accessed:</strong> ${interaction.startTime ? new Date(interaction.startTime).toLocaleDateString() : 'N/A'}</div>
              </div>
              <div class="mt-3 lg:mt-0">
                <a href="/content/${interaction.contentListingId || ''}" target="_blank" class="inline-flex items-center px-3 py-2 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-gray-50">
                  View Content
                </a>
              </div>
            </div>
          </div>
        `).join('');
      }
    }
  };

  // Function to populate purchases data
  window.populatePurchases = function(purchases) {
    const list = document.getElementById('purchases-list');
    const count = document.getElementById('purchases-count');
    const loading = document.getElementById('purchases-loading');
    const empty = document.getElementById('purchases-empty');
    
    if (loading) loading.style.display = 'none';
    if (count) count.textContent = purchases.length;
    
    if (purchases.length === 0) {
      if (empty) empty.style.display = 'block';
      if (list) list.style.display = 'none';
    } else {
      if (empty) empty.style.display = 'none';
      if (list) {
        list.style.display = 'block';
        list.innerHTML = purchases.map(purchase => `
          <div class="bg-white rounded-lg p-6 shadow-sm">
            <div class="text-xl font-bold mb-3">${purchase.contentListingName || 'Purchase'}</div>
            <div class="flex flex-wrap items-center justify-between">
              <div class="text-sm text-gray-700 space-y-1">
                <div><strong>Order Number:</strong> ${purchase.orderNumber || 'N/A'}</div>
                <div><strong>Order Date:</strong> ${purchase.orderDate ? new Date(purchase.orderDate).toLocaleDateString() : 'N/A'}</div>
                <div><strong>Payment Status:</strong> ${purchase.cardPaymentStatus || 'N/A'}</div>
                <div><strong>Total:</strong> $${purchase.total || '0.00'}</div>
              </div>
              <div class="mt-3 lg:mt-0">
                <a href="/content/${purchase.contentListingId || ''}" target="_blank" class="inline-flex items-center px-3 py-2 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-gray-50">
                  View Content
                </a>
              </div>
            </div>
          </div>
        `).join('');
      }
    }
  };

  export default {
    components: {
      TranscriptTable,
    },
    setup() {
      // Reactive data
      const earnedCertificates = ref([]);
      const interactions = ref([]);
      const purchases = ref([]);
      const loadingCertificates = ref(true);
      const loadingInteractions = ref(true);
      const loadingPurchases = ref(true);
      const uploadingFile = ref(false);
      const savingChanges = ref(false);
      const contactInformationError = ref('');
      const successMessage = ref('');
      const activeTab = ref(0);
      const total = ref(0);
      
      // Email change state
      const newEmail = ref('');
      const requestingEmailChange = ref(false);
      const emailChangeError = ref('');
      const emailChangeSuccess = ref('');
      const emailChangePending = ref(false);
      const pendingNewEmail = ref('');
      
      const form = reactive({
        profileImageUrl: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        fullName: ''
      });
      
      const pagination = reactive({
        page: 1,
        pageSize: 10,
        sortField: 'createdAt',
        sortOrder: 'desc',
      });

      // Auth store
      const toast = useToast();
      const authStore = useAuthStore();

      // Computed properties
      const profile = computed(() => authStore?.profile || {});
      const thumbnailUrl = computed(() => 
        form.profileImageUrl ? `${form.profileImageUrl}?${new Date().getTime()}` : ''
      );

      // Watch pagination changes
      watch(pagination, () => {
        getPurchases();
      }, { deep: true });

      // Methods
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      };

      const fetchEarnedCertificates = async () => {
        try {
          const { data } = await axios.get('/api/earned-certificates/user');
          console.log('Earned certificates data:', data);
          earnedCertificates.value = data;
          
          // Call the JavaScript function to populate the UI
          setTimeout(() => {
            if (typeof window.populateCertificates === 'function') {
              window.populateCertificates(data);
            }
            const transcriptEmpty = document.getElementById('transcript-empty');
            const transcriptContent = document.getElementById('transcript-content');
            if (data.length === 0) {
              if (transcriptEmpty) transcriptEmpty.style.display = 'block';
              if (transcriptContent) transcriptContent.style.display = 'none';
            } else {
              if (transcriptEmpty) transcriptEmpty.style.display = 'none';
              if (transcriptContent) transcriptContent.style.display = 'block';
            }
          }, 100);
        } catch (error) {
          console.error('Error fetching earned certificates:', error);
          toast.error('Failed to load your certificates. Please refresh the page and try again.');
          earnedCertificates.value = [];
          setTimeout(() => {
            if (typeof window.populateCertificates === 'function') {
              window.populateCertificates([]);
            }
            const transcriptEmpty = document.getElementById('transcript-empty');
            const transcriptContent = document.getElementById('transcript-content');
            if (transcriptEmpty) {
              transcriptEmpty.textContent = 'Your certificates could not be loaded. Please refresh the page and try again.';
              transcriptEmpty.style.display = 'block';
            }
            if (transcriptContent) transcriptContent.style.display = 'none';
          }, 100);
        } finally {
          loadingCertificates.value = false;
        }
      };

      const fetchInteractions = async () => {
        try {
          const { data } = await axios.get('/api/content-listings/interactions/user');
          console.log('Interactions data:', data);
          interactions.value = data;
          
          // Call the JavaScript function to populate the UI
          setTimeout(() => {
            if (typeof window.populateInteractions === 'function') {
              window.populateInteractions(data);
            }
          }, 100);
        } catch (error) {
          console.error('Error fetching interactions:', error);
          interactions.value = [];
          setTimeout(() => {
            if (typeof window.populateInteractions === 'function') {
              window.populateInteractions([]);
            }
          }, 100);
        } finally {
          loadingInteractions.value = false;
        }
      };

      const getPurchases = async () => {
        try {
          const {
            page, sortField, sortOrder, pageSize,
          } = pagination;
          const params = [`sortDir=${sortField} ${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`].join('&');
          const { data } = await axios.get(`/api/purchases/paged?${params}`);
          console.log('Purchases data:', data);
          purchases.value = data.resultItems;
          total.value = data.totalRecords;
          
          // Call the JavaScript function to populate the UI
          setTimeout(() => {
            if (typeof window.populatePurchases === 'function') {
              window.populatePurchases(data.resultItems);
            }
          }, 100);
        } catch (error) {
          console.error('Error fetching purchases:', error);
          purchases.value = [];
          total.value = 0;
          setTimeout(() => {
            if (typeof window.populatePurchases === 'function') {
              window.populatePurchases([]);
            }
          }, 100);
        } finally {
          loadingPurchases.value = false;
        }
      };

      // Initialize on mount
      onMounted(() => {
        console.log('ProfilePage mounted, initializing...');
        console.log('Auth store:', authStore);
        console.log('Profile before copy:', profile.value);
        
        // Wait for profile to be available, then copy it
        if (profile.value && Object.keys(profile.value).length > 0) {
          Object.assign(form, JSON.parse(JSON.stringify(profile.value)));
        }
        
        console.log('Form after initialization:', form);
        console.log('Starting API calls...');
        fetchEarnedCertificates();
        fetchInteractions();
        getPurchases();
      });

      const uploadImage = async () => {
        uploadingFile.value = true;
        const file = document.querySelector('input[type="file"]').files[0];
        const formData = new FormData();
        formData.append('image', file);
        try {
          const { data } = await axios.put('/api/user/profile/image', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          form.profileImageUrl = data.profileImageUrl;
          if (authStore && typeof authStore.setUserProfile === 'function') {
            authStore.setUserProfile({
              ...form,
              fullName: `${form.firstName} ${form.lastName}`
            });
          }
          // Note: Replace with proper toast notification in Vue 3
          console.log('Image was successfully uploaded');
        } catch (error) {
          console.error('Upload error:', error);
        } finally {
          resetFileInput();
          uploadingFile.value = false;
        }
      };

      const saveProfile = async () => {
        savingChanges.value = true;
        contactInformationError.value = '';
        successMessage.value = '';
        try {
          const updateData = {
            firstName: form.firstName?.trim(),
            lastName: form.lastName?.trim(),
            phoneNumber: form.phoneNumber?.trim()
          };
          
          const { data } = await axios.patch('/api/user/profile', updateData);
          
          // Update form with response data
          Object.assign(form, {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            email: data.email,
            profileImageUrl: data.profileImageUrl
          });
          
          // Update auth store
          if (authStore && typeof authStore.setUserProfile === 'function') {
            authStore.setUserProfile(data);
          }
          
          successMessage.value = 'Profile updated successfully!';
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            successMessage.value = '';
          }, 5000);
        } catch (error) {
          console.error('Save profile error:', error);
          if (error.response?.data && Array.isArray(error.response.data)) {
            contactInformationError.value = error.response.data.join(' ');
          } else if (error.response?.data?.message) {
            contactInformationError.value = error.response.data.message;
          } else {
            contactInformationError.value = 'An error occurred while saving your profile. Please try again.';
          }
        } finally {
          savingChanges.value = false;
        }
      };

      const requestEmailChange = async () => {
        requestingEmailChange.value = true;
        emailChangeError.value = '';
        emailChangeSuccess.value = '';
        emailChangePending.value = false;
        
        try {
          await axios.post('/api/user/profile/email/request', {
            newEmail: newEmail.value
          });
          
          // Show pending state
          emailChangePending.value = true;
          pendingNewEmail.value = newEmail.value;
          emailChangeSuccess.value = 'Email change request sent! Please check your inbox for a confirmation link.';
          newEmail.value = '';
          
          // Clear success message after 10 seconds
          setTimeout(() => {
            emailChangeSuccess.value = '';
          }, 10000);
        } catch (error) {
          console.error('Request email change error:', error);
          if (error.response?.data && Array.isArray(error.response.data)) {
            emailChangeError.value = error.response.data.join(' ');
          } else if (error.response?.data?.message) {
            emailChangeError.value = error.response.data.message;
          } else {
            emailChangeError.value = 'An error occurred while requesting the email change. Please try again.';
          }
        } finally {
          requestingEmailChange.value = false;
        }
      };

      const showDeleteImagePrompt = async () => {
        // Note: Replace with proper dialog in Vue 3
        if (confirm('Are you sure you want to delete your profile picture? This action cannot be undone.')) {
          try {
            await axios.delete('/api/user/profile/image');
            form.profileImageUrl = '';
            if (authStore && typeof authStore.setUserProfile === 'function') {
              authStore.setUserProfile({
                ...form,
                fullName: `${form.firstName} ${form.lastName}`
              });
            }
            console.log('Profile picture was removed');
          } catch (error) {
            console.error('Delete error:', error);
          }
        }
      };

      const resetFileInput = () => {
        const input = document.querySelector('input[type="file"]');
        if (input) {
          input.type = 'text';
          input.type = 'file';
          input.value = '';
        }
      };

      const switchTab = (tabIndex) => {
        console.log('Switching to tab:', tabIndex);
        
        // Hide all tab content
        for (let i = 0; i < 4; i++) {
          const content = document.getElementById('tab-content-' + i);
          const button = document.getElementById('tab-btn-' + i);
          if (content) content.style.display = 'none';
          if (button) {
            button.className = 'py-2 px-1 border-b-2 font-medium text-sm border-transparent text-primary hover:text-gray-700 hover:border-gray-300';
          }
        }
        
        // Show selected tab content
        const selectedContent = document.getElementById('tab-content-' + tabIndex);
        const selectedButton = document.getElementById('tab-btn-' + tabIndex);
        if (selectedContent) {
          selectedContent.style.display = 'block';
          console.log('Showing tab content:', tabIndex);
        }
        if (selectedButton) {
          selectedButton.className = 'py-2 px-1 border-b-2 font-medium text-sm border-primary text-primary';
        }
      };

      // Return everything that the template needs
      return {
        // Data
        earnedCertificates,
        interactions,
        purchases,
        loadingCertificates,
        loadingInteractions,
        loadingPurchases,
        uploadingFile,
        savingChanges,
        contactInformationError,
        successMessage,
        activeTab,
        form,
        total,
        pagination,
        newEmail,
        requestingEmailChange,
        emailChangeError,
        emailChangeSuccess,
        emailChangePending,
        pendingNewEmail,
        
        // Computed
        profile,
        thumbnailUrl,
        
        // Methods
        formatDate,
        fetchEarnedCertificates,
        fetchInteractions,
        getPurchases,
        uploadImage,
        saveProfile,
        requestEmailChange,
        showDeleteImagePrompt,
        resetFileInput,
        switchTab
      };
    },
  };
</script>
